/* eslint-disable no-param-reassign */
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { FormatDateOptions, IntlShape } from "react-intl";
import { defaultUnits, explicitConvertValue } from "../../utils/unit-utils";
import { UserProfilePrefs } from "../../../modules/Helpers/UserProfilePrefs";
import lookupValues from "../../helpers/lookupValues";
import { translateKeyMapping } from "../../utils/string-utils";
import { FootprintCategoryModel, FootprintModel } from "../../models/Footprint";
import {
  FOOTPRINT_CATEGORIES,
  FootprintCategoryName,
  getCompartmentsForImpactCategory,
} from "../../models/Footprint/FootprintTypes";
import {
  DatasetOption,
  isFarmDataset,
} from "../../utils/footprint-compare-utils";
import { dateOnlyDisplayOptionsShort } from "../../utils/datetime-utils";
import {
  AnimalType,
  FootPrintType,
  StageType,
} from "../../../../graphql/types";

const allowAnimalTypesForTemplateExport = [
  AnimalType.Beef,
  AnimalType.Pig,
  AnimalType.SalmonV2,
  AnimalType.SeaBass,
  AnimalType.SeaBream,
  AnimalType.BroilerV2,
  AnimalType.LayingHensV2,
  AnimalType.Turkey,
  AnimalType.DairyV2,
  AnimalType.LitopenaeusVannamei,
  AnimalType.PenaeusMonodon,
];

function addCategorySheet(
  intl: IntlShape,
  workbook: Excel.Workbook,
  category: FootprintCategoryName,
  data: ViewData,
  convRatio: number,
) {
  const translatedCategoryName = intl.formatMessage({
    id: `REPORT.FOOTPRINTS.${translateKeyMapping(category)}`,
    defaultMessage: category,
  });
  const sheet = workbook.addWorksheet(translatedCategoryName);

  sheet.getColumn(1).width = 40;
  sheet.getColumn(2).width = 15;
  sheet.getColumn(3).width = 15;
  sheet.getColumn(4).width = 15;
  sheet.getColumn(5).width = 15;

  // header
  sheet.getCell("A1").value = translatedCategoryName;
  sheet.getCell("A1").style = { font: { bold: true } };
  sheet.getCell("B1").value = data.footprint.name;
  sheet.mergeCells("B1:C1");
  sheet.getCell("B1").style = {
    font: { bold: true },
    alignment: { horizontal: "center" },
  };

  if (data.comparison) {
    sheet.getCell("D1").value = data.comparison?.name;
    sheet.mergeCells("D1:E1");
    sheet.getCell("D1").style = {
      font: { bold: true },
      alignment: { horizontal: "center" },
    };
    sheet.getCell("G1").value = intl.formatMessage({
      id: "REPORT.FOOTPRINTS.CHANGE_DIFFERENCE",
    });
    sheet.getColumn(7).width = 35;
    sheet.getCell("G1").style = {
      font: { bold: true },
      alignment: { horizontal: "center" },
    };
  }
  let totalBaseline = 0;
  let totalComparison = 0;

  // data rows
  data.impactComponents[category].forEach((impactData) => {
    const rowValues = [];

    const impactAbsolute =
      convRatio !== 1 && impactData.impactAbsolute
        ? impactData.impactAbsolute * convRatio
        : impactData.impactAbsolute;

    const comparedImpactAbsolute =
      convRatio !== 1 && impactData.comparedImpactAbsolute
        ? impactData.comparedImpactAbsolute * convRatio
        : impactData.comparedImpactAbsolute;

    rowValues.push(`${impactData.label} (${impactData.unit})`);
    rowValues.push(impactAbsolute);
    rowValues.push(impactData.impact ? impactData.impact / 100 : 0);
    totalBaseline += impactAbsolute || 0;

    if (data.comparison) {
      rowValues.push(comparedImpactAbsolute);
      rowValues.push(
        impactData.comparedImpact ? impactData.comparedImpact / 100 : 0,
      );
      totalComparison += comparedImpactAbsolute || 0;
      if (comparedImpactAbsolute && impactAbsolute) {
        const diffPercentage =
          (comparedImpactAbsolute - impactAbsolute) / impactAbsolute;
        rowValues.push("");
        rowValues.push(diffPercentage);
      }
    }

    const row = sheet.addRow(rowValues);
    row.getCell(3).numFmt = "0.00%";
    row.getCell(5).numFmt = "0.00%";
    row.getCell(7).numFmt = "0.00%";
  });
  let totalDiff = 0;

  if (totalComparison) {
    totalDiff = (totalComparison - totalBaseline) / totalBaseline;
  }

  const rowValues = [];
  rowValues.push(intl.formatMessage({ id: "GENERAL.TOTAL" }));
  rowValues.push(totalBaseline);
  rowValues.push("");
  rowValues.push(totalComparison || "");
  rowValues.push("");
  rowValues.push("");
  rowValues.push(totalDiff || "");

  sheet.addRow([]);
  const row = sheet.addRow(rowValues);
  row.getCell(3).numFmt = "0.00%";
  row.getCell(5).numFmt = "0.00%";
  row.getCell(7).numFmt = "0.00%";

  return sheet;
}

const getCategoryId = (category: string) => {
  const categoryId = category
    ? category
        .toUpperCase()
        .replaceAll(" - ", "_")
        .replaceAll("-", "_")
        .replaceAll(", ", "_")
        .replaceAll(" ", "_")
    : category;
  return `REPORT.FOOTPRINTS.TEMPLATE.${categoryId}`;
};

interface CategorySheetOptions {
  stageType?: StageType | null;
  baseline?: DatasetOption | null;
}

function editCategorySheet(
  intl: IntlShape,
  workbook: Excel.Workbook,
  category: FootprintCategoryName,
  data: ViewData,
  convRatio: number,
  userUnitEnvImpactPer: string,
  options?: CategorySheetOptions,
) {
  const translatedCategoryName = intl.formatMessage({
    id: getCategoryId(category),
    defaultMessage: category,
  });
  const sheet = workbook.getWorksheet(translatedCategoryName);

  if (!sheet) {
    return;
  }

  let row = 15;

  data.impactComponents[category].sort(sortCategoryComparator);

  data.impactComponents[category].forEach((impactData) => {
    const impactAbsolute =
      convRatio !== 1 && impactData.impactAbsolute
        ? impactData.impactAbsolute * convRatio
        : impactData.impactAbsolute;

    sheet.getCell(`B${row}`).value = `${impactData.label} (${impactData.unit})`;
    sheet.getCell(`C${row}`).value = impactAbsolute;

    row += 1;
  });

  const shouldUseProduced =
    options?.stageType === StageType.Processing ||
    (options?.baseline &&
      isFarmDataset(options.baseline) &&
      options.baseline.stageName &&
      (options.baseline.stageName.includes("Hatching egg") ||
        options.baseline.stageName.includes("Consumption egg")));

  if (shouldUseProduced) {
    sheet.getCell("F9").value = userUnitEnvImpactPer;
    sheet.getCell("G9").value = intl.formatMessage({
      id: "REPORT.FOOTPRINTS.TEMPLATE.PRODUCED",
    });
  } else if (
    options?.stageType === StageType.DairyCow &&
    options?.baseline &&
    isFarmDataset(options.baseline) &&
    options.baseline.stageName?.includes("Milk")
  ) {
    sheet.getCell("F9").value = userUnitEnvImpactPer;
    sheet.getCell("G9").value = intl.formatMessage({
      id: "REPORT.FOOTPRINTS.TEMPLATE.FPCM",
    });
  } else {
    sheet.getCell("F9").value = userUnitEnvImpactPer;
    sheet.getCell("G9").value = intl.formatMessage({
      id: "REPORT.FOOTPRINTS.TEMPLATE.LIVEWEIGHT",
    });
  }

  return sheet;
}

const addDatasetSummary = (
  sheet: Excel.Worksheet,
  intl: IntlShape,
  data: ViewData,
  dataset: DatasetOption | null | undefined,
  firstColumn = "A",
  secondColumn = "B",
  isComparison = false,
) => {
  if (!isComparison) {
    sheet.getCell(`${firstColumn}1`).value = intl.formatMessage({
      id: "SUSTELL.DASHBOARD.EXPORT.DATE_OF",
    });
    sheet.getCell(`${secondColumn}1`).value = intl.formatDate(
      new Date(),
      dateOnlyDisplayOptionsShort as FormatDateOptions,
    );
  }

  sheet.getCell(`${firstColumn}2`).value = intl.formatMessage({
    id: "GENERAL.NAME",
  });
  sheet.getCell(`${secondColumn}2`).value = dataset?.displayName;

  let type = intl.formatMessage({ id: "SUSTELL.STAGE.FEED.COMPOUND_FEED" });
  if (data.footprint.type !== FootPrintType.CompoundFeed) {
    type = intl.formatMessage({ id: "FARM" });
  }

  sheet.getCell(`${firstColumn}3`).value = intl.formatMessage({
    id: "GENERAL.TYPE",
  });
  sheet.getCell(`${secondColumn}3`).value = type;

  sheet.getCell(`${firstColumn}4`).value = intl.formatMessage({
    id: "GENERAL.YEAR",
  });
  sheet.getCell(`${secondColumn}4`).value = dataset?.year
    ? Number(dataset.year)
    : null;

  if (data.footprint.type !== FootPrintType.CompoundFeed) {
    sheet.getCell(`${firstColumn}5`).value = intl.formatMessage({
      id: "BASELINE.FORM.FIELD.START_DATE",
    });
    sheet.getCell(`${secondColumn}5`).value =
      dataset && isFarmDataset(dataset) && dataset.validFrom
        ? intl.formatDate(
            dataset.validFrom,
            dateOnlyDisplayOptionsShort as FormatDateOptions,
          )
        : "";

    sheet.getCell(`${firstColumn}6`).value = intl.formatMessage({
      id: "BASELINE.FORM.FIELD.END_DATE",
    });
    sheet.getCell(`${secondColumn}6`).value =
      dataset && isFarmDataset(dataset) && dataset.validTo
        ? intl.formatDate(
            dataset.validTo,
            dateOnlyDisplayOptionsShort as FormatDateOptions,
          )
        : "";

    if (dataset && isFarmDataset(dataset) && dataset.stageName) {
      sheet.getCell(`${firstColumn}7`).value = intl.formatMessage({
        id: "REPORT.FOOTPRINTS.STAGE_SELECTION",
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      sheet.getCell(`${secondColumn}7`).value = dataset.stageName;
    }
  }
};

const addSummarySheet = (
  intl: IntlShape,
  workbook: Excel.Workbook,
  data: ViewData,
  convRatio: number,
  userUnitEnvImpactPer: string,
  baseline: DatasetOption | null | undefined,
  comparison: DatasetOption | null | undefined,
) => {
  const sheet = workbook.addWorksheet(
    intl.formatMessage({ id: "GENERAL.SUMMARY" }),
  );

  addDatasetSummary(sheet, intl, data, baseline);

  if (comparison) {
    addDatasetSummary(sheet, intl, data, comparison, "D", "E", true);
  }

  const startRow = 9;

  sheet.getRow(startRow).font = { bold: true };
  sheet.getCell(`A${startRow}`).value = intl.formatMessage({
    id: "REPORT.FOOTPRINTS.IMPACT_CATEGORY",
  });
  sheet.getCell(`B${startRow}`).value = intl.formatMessage({
    id: "REPORT.FOOTPRINTS.EXPORT_TITLE",
  });

  if (data?.comparison) {
    sheet.mergeCells(`B${startRow}:C${startRow}`);
    sheet.getCell(`B${startRow}`).style = {
      font: { bold: true },
      alignment: { horizontal: "center" },
    };
    sheet.getCell(`D${startRow}`).value = intl.formatMessage(
      { id: "REPORT.FOOTPRINTS.UNITS" },
      { userUnits: userUnitEnvImpactPer },
    );
    sheet.getCell(`E${startRow}`).value = intl.formatMessage({
      id: "REPORT.FOOTPRINTS.CHANGE",
    });
  } else {
    sheet.getCell(`C${startRow}`).value = intl.formatMessage(
      { id: "REPORT.FOOTPRINTS.UNITS" },
      { userUnits: userUnitEnvImpactPer },
    );
  }

  data.impactCategories.forEach((category) => {
    const rowValues = [];
    rowValues.push(
      intl.formatMessage({
        id: `REPORT.FOOTPRINTS.${translateKeyMapping(category.category_name)}`,
        defaultMessage: category.category_name,
      }),
    );
    rowValues.push(
      convRatio !== 1 && category.value
        ? category.value * convRatio
        : category.value,
    );

    if (data?.comparison) {
      rowValues.push(
        convRatio !== 1 && category.comparedValue
          ? category.comparedValue * convRatio
          : category.comparedValue,
      );
      rowValues.push(category.unit);
      rowValues.push(category.diff);
    } else {
      rowValues.push(category.unit);
    }
    const row = sheet.addRow(rowValues);
    row.getCell(5).numFmt = "0.00";
  });

  sheet.getColumn(1).width = 40;
  sheet.getColumn(2).width = 15;
  sheet.getColumn(3).width = 15;
  if (data?.comparison) {
    sheet.getColumn(4).width = 15;
    sheet.getColumn(5).width = 15;
  }

  return sheet;
};

const editSummarySheet = (
  intl: IntlShape,
  workbook: Excel.Workbook,
  data: ViewData,
  convRatio: number,
  baseline: DatasetOption | null | undefined,
  hasErrors = false,
  isFlag = false,
) => {
  const sheet = workbook.getWorksheet(
    intl.formatMessage({ id: "GENERAL.SUMMARY" }),
  );

  let type = intl.formatMessage({ id: "SUSTELL.STAGE.FEED.COMPOUND_FEED" });
  if (data.footprint.type !== FootPrintType.CompoundFeed) {
    type = intl.formatMessage({ id: "FARM" });
  }

  sheet.getCell("C2").value = intl.formatDate(
    new Date(),
    dateOnlyDisplayOptionsShort as FormatDateOptions,
  );
  sheet.getCell("C4").value = type;
  sheet.getCell("C6").value = baseline?.displayName;

  const yearCell = sheet.getCell("C8");
  yearCell.value = baseline?.year ? Number(baseline.year) : null;

  if (data.footprint.type !== FootPrintType.CompoundFeed) {
    sheet.getCell("C10").value =
      baseline && isFarmDataset(baseline) && baseline.validFrom
        ? intl.formatDate(
            baseline.validFrom,
            dateOnlyDisplayOptionsShort as FormatDateOptions,
          )
        : "";
    sheet.getCell("C12").value =
      baseline && isFarmDataset(baseline) && baseline.validTo
        ? intl.formatDate(
            baseline.validTo,
            dateOnlyDisplayOptionsShort as FormatDateOptions,
          )
        : "";

    if (baseline && isFarmDataset(baseline) && baseline.stageName) {
      sheet.getCell("C14").value = baseline.stageName;
    }
  }

  if (!hasErrors) {
    const templateCategories = isFlag
      ? lookupValues.templateFLAGCategoryOrder
      : lookupValues.templateCategoryOrder;

    let row = 21;

    templateCategories.forEach((categoryName) => {
      const category = data.impactCategories.find(
        (cat) => cat.category_name === categoryName,
      );
      if (category) {
        const value =
          convRatio !== 1 && category.value
            ? category.value * convRatio
            : category.value;

        if (value !== null && value !== undefined && value !== 0) {
          const cellC = sheet.getCell(`C${row}`);
          cellC.value = Number(value);
        }
      }
      row += 1;
    });
  } else {
    for (let row = 21; row <= 60; row++) {
      const cellC = sheet.getCell(`C${row}`);
      if (
        cellC.value !== null &&
        cellC.value !== undefined &&
        cellC.value !== ""
      ) {
        cellC.value = "";
      }
    }
  }

  return sheet;
};

const sortComparator = (row1: ImpactCategory, row2: ImpactCategory) => {
  const a = row1.category_name || "";
  const b = row2.category_name || "";
  return a.localeCompare(b);
};

const sortCategoryComparator = (
  row1: ImpactComponentData,
  row2: ImpactComponentData,
) => {
  const a = row1.label ? row1.label : 0;
  const b = row2.label ? row2.label : 0;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

export default async function FootprintExcelExport(
  intl: IntlShape,
  data: ViewData,
  baseline: DatasetOption | null | undefined,
  comparison: DatasetOption | null | undefined,
): Promise<void> {
  const workbook = new Excel.Workbook();
  const { convRatio, userUnitEnvImpactPer = "kg" } = data.conversionSetup;

  addSummarySheet(
    intl,
    workbook,
    data,
    convRatio,
    userUnitEnvImpactPer,
    baseline,
    comparison,
  );

  data.impactCategories.forEach((category) =>
    addCategorySheet(intl, workbook, category.category_name, data, convRatio),
  );

  await workbook.xlsx.writeBuffer().then((resultData) => {
    const blob = new Blob([resultData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "footprint.xlsx");
  });
}

export async function FootprintExcelTemplateExport(
  intl: IntlShape,
  data: ViewData,
  baseline: DatasetOption | null | undefined,
): Promise<void> {
  try {
    const language = intl.formatMessage({
      id: "REPORT.FOOTPRINTS.TEMPLATE.LANGUAGE",
    });
    const animalType =
      baseline && isFarmDataset(baseline)
        ? lookupValues.animalTypeTemplates[baseline.animalType]
        : undefined;
    const isFlag = data.footprint.categories?.some(
      (category) =>
        category.key === FOOTPRINT_CATEGORIES["Climate change - Total"],
    );
    const templatePath = isFlag
      ? `/media/excel_templates/(${language}) Export templates_Farm (${animalType}) FLAG.xlsx`
      : `/media/excel_templates/(${language}) Export templates_Farm (${animalType}).xlsx`;

    const file = await fetch(templatePath);

    if (!file.ok) {
      throw new Error(`Template file not found: ${templatePath}`);
    }

    const workbook = new Excel.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());

    const { convRatio, userUnitEnvImpactPer = "kg" } = data.conversionSetup;

    const hasErrors =
      data.footprint.calculationErrors?.length > 0 ||
      !data.footprint.unFilteredCategories?.length ||
      data.impactCategories.length === 0 ||
      data.impactCategories.every((cat) => !cat.value || cat.value === 0) ||
      !data.impactComponents ||
      Object.keys(data.impactComponents).length === 0;

    editSummarySheet(
      intl,
      workbook,
      data,
      convRatio,
      baseline,
      hasErrors,
      isFlag,
    );

    if (!hasErrors) {
      data.impactCategories.forEach((category) => {
        try {
          editCategorySheet(
            intl,
            workbook,
            category.category_name,
            data,
            convRatio,
            userUnitEnvImpactPer,
            {
              stageType:
                baseline && isFarmDataset(baseline)
                  ? baseline.stageType
                  : undefined,
              baseline,
            },
          );
        } catch (error) {
          console.error("Error processing category: ", category, error);
        }
      });
    }

    await workbook.xlsx.writeBuffer().then((resultData) => {
      const blob = new Blob([resultData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "footprint.xlsx");
    });
  } catch (error) {
    console.error("Template export failed:", error);
    await FootprintExcelExport(intl, data, baseline, undefined);
  }
}

type ImpactCategory = {
  category_name: string;
  compartments: string[];
  value?: number;
  comparedValue: number | null;
  diff: number | null;
  unit: string | null;
};

type ConversionSetup = {
  convRatio: number;
  defaultUnitEnvImpactPer: string;
  userUnitEnvImpactPer: string;
};

type ViewData = {
  footprint: FootprintModel;
  isComparison: boolean;
  impactCategories: ImpactCategory[];
  impactComponents: { [key in FootprintCategoryName]: ImpactComponentData[] };
  selectedFootprint?: FootprintModel | null;
  selectedComparison?: FootprintModel | null;
  comparison?: FootprintModel | null;
  conversionSetup: ConversionSetup;
};

type ImpactComponentData = {
  label: string;
  impact?: number | null;
  impactAbsolute?: number | null;
  unit: string;
  comparedImpact?: number | null;
  comparedImpactAbsolute?: number | null;
};

const prepExportData = (footprint: FootprintModel): ViewData => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const userUOM = userProfile.getUserUnitPrefs();
  const convRatio =
    !userUOM ||
    (userUOM && userUOM.unitEnvImpactPer === defaultUnits.unitEnvImpactPer)
      ? 1
      : explicitConvertValue(
          1,
          userUOM.unitEnvImpactPer,
          defaultUnits.unitEnvImpactPer,
        ) || 1;

  const viewData: ViewData = {
    footprint,
    isComparison: !!footprint.comparison,
    impactCategories: [],
    impactComponents: {} as {
      [key in FootprintCategoryName]: ImpactComponentData[];
    },
    selectedFootprint: null,
    selectedComparison: null,
    comparison: null,
    conversionSetup: {} as ConversionSetup,
  };

  if (footprint == null) {
    return viewData;
  }

  const selectedFootprint = footprint;

  viewData.selectedFootprint = selectedFootprint;
  let selectedComparison = null;

  if (viewData.isComparison) {
    selectedComparison = footprint.comparison;

    viewData.selectedComparison = selectedComparison;
    viewData.comparison = footprint.comparison;
  }

  const impactCategoriesMapping = lookupValues.impactCategoriesMapping.all;
  const footprintCategories = selectedFootprint.unFilteredCategories;

  footprintCategories.forEach((category) => {
    let comparedCategory: FootprintCategoryModel | null | undefined = null;
    if (viewData.isComparison) {
      comparedCategory = category.comparison;
    }

    if (category.key in impactCategoriesMapping) {
      let impactCategoryHasData = false;

      category.componentsUnfiltered.forEach((component) => {
        if (component.amount === 0.0) {
          return;
        }

        if (category.key in viewData.impactComponents === false)
          viewData.impactComponents[category.key] = [];

        let comparedImpactComponent = null;
        if (viewData.isComparison && comparedCategory) {
          comparedImpactComponent = component.comparison;
        }

        const impactComponentData: ImpactComponentData = {
          label: component.name,
          impact: component.percentage,
          impactAbsolute: component.amount,
          unit: category.unit,
          comparedImpact: null,
          comparedImpactAbsolute: null,
        };
        if (viewData.isComparison) {
          if (comparedImpactComponent) {
            impactComponentData.comparedImpact =
              comparedImpactComponent.percentage;
            impactComponentData.comparedImpactAbsolute =
              comparedImpactComponent.amount;
          } else {
            impactComponentData.comparedImpact = null;
          }
        }

        viewData.impactComponents[category.key].push(impactComponentData);

        impactCategoryHasData = true;
      });

      if (viewData.isComparison && comparedCategory) {
        const impactLabels = category.components.map((el) => el.label);
        const unmatchedComparedImpacts = comparedCategory.components.filter(
          (el) => !impactLabels.includes(el.label),
        );
        unmatchedComparedImpacts.forEach((impactComponent) => {
          if (impactComponent.percentage === 0.0) {
            return;
          }
          if (!(category.key in viewData.impactComponents))
            viewData.impactComponents[category.key] = [];

          const impactComponentData = {
            label: impactComponent.label,
            impact: null,
            comparedImpact: impactComponent.percentage,
            comparedImpactAbsolute: impactComponent.amount,
            unit: category.unit,
          };

          viewData.impactComponents[category.key].push(impactComponentData);
          impactCategoryHasData = true;
        });
      }

      if (impactCategoryHasData) {
        viewData.impactCategories.push({
          category_name: category.key,
          compartments: getCompartmentsForImpactCategory(category.key),
          value: category.value,
          comparedValue:
            viewData.isComparison && comparedCategory?.value
              ? comparedCategory?.value
              : null,
          diff:
            viewData.isComparison && comparedCategory?.value
              ? category.comparisonChangePercentage
              : null,
          unit: category.unit,
        });
      }
    }

    viewData.impactComponents[category.key].sort((a, b) => {
      if (a.impact && b.impact && a.impact > b.impact) {
        return -1;
      }
      if (b.impact && a.impact && b.impact > a.impact) {
        return 1;
      }
      return 0;
    });
  });

  viewData.impactCategories.sort(sortComparator);
  viewData.conversionSetup = {
    convRatio,
    defaultUnitEnvImpactPer: defaultUnits.unitEnvImpactPer,
    userUnitEnvImpactPer: userUOM.unitEnvImpactPer,
  };
  return viewData;
};

export const exportToExcel = async (
  intl: IntlShape,
  footprint: FootprintModel,
  baseline: DatasetOption | null | undefined,
  comparison: DatasetOption | null | undefined,
) => {
  const exportData = prepExportData(footprint);

  const animalType =
    baseline && isFarmDataset(baseline) ? baseline.animalType : undefined;
  const shouldUseTemplate =
    animalType &&
    allowAnimalTypesForTemplateExport.includes(animalType) &&
    !exportData.isComparison;

  if (shouldUseTemplate) {
    await FootprintExcelTemplateExport(intl, exportData, baseline);
  } else {
    await FootprintExcelExport(intl, exportData, baseline, comparison);
  }
};
